import { useCallback, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import MediaAPI from "../../../api/MediaAPI";
import { SmartlistType } from "../../../api/MediaAPI.types";
import SmartlistAPI from "../../../api/SmartlistAPI";
import { navigatorLanguage } from "../../../i18n";
import {
  getFreeType,
  getTranslation,
  isAudioContent,
  isFree,
} from "../../../utils/data/Content";
import ItemStatus from "../../../utils/ItemStatus";
import { CONTENT_TYPE } from "../../content/Type";
import matchesItemSearch from "../selectBoxItemSearch";
import { SELECT_BOX_TYPES } from "../types/selectBoxTypes";

import type { Dispatch, SetStateAction } from "react";

import type { AppState } from "../../../reducers";
import type {
  SelectBoxDisplayItem,
  SelectBoxListFlags,
  SelectBoxTab,
} from "../selectBox.types";
import type { SelectBoxType } from "../types/selectBoxTypes";

const { SMARTLISTS, OFFERS, SEASONS, FREE } = SELECT_BOX_TYPES;
const { MASTER } = CONTENT_TYPE;

export type SelectBoxFilterKey =
  | "not_free"
  | "not_free_registered"
  | "not_offers"
  | "not_in_offer";

export interface UseLibraryItemsParams extends SelectBoxListFlags {
  selectedItems: SelectBoxDisplayItem[];
  filters?: SelectBoxFilterKey[];
  multiSelectList: SelectBoxDisplayItem[];
  ignoreSmartlistsAudios?: boolean;
  selectBoxType?: SelectBoxType;
}

export interface UseLibraryItemsResult {
  libraryItems: SelectBoxDisplayItem[];
  libraryStatus: string;
  setLibraryStatus: Dispatch<SetStateAction<string>>;
  librarySearch: string;
  setLibrarySearch: Dispatch<SetStateAction<string>>;
  libraryFilteredItems: SelectBoxDisplayItem[];
  selectedTab: SelectBoxTab | undefined;
  setSelectedTab: Dispatch<SetStateAction<SelectBoxTab | undefined>>;
}

function searchLibraryItems(
  itemList: SelectBoxDisplayItem[],
  searchText: string,
): SelectBoxDisplayItem[] {
  return itemList.filter((item) => {
    const translationName = getTranslation(
      item,
      navigatorLanguage,
    )?.name?.toLowerCase();

    return (
      matchesItemSearch(item, searchText) ||
      Boolean(translationName?.includes(searchText))
    );
  });
}

interface LibraryFilterParams {
  filters?: SelectBoxFilterKey[];
  freecontents: AppState["fetchReducer"]["freecontents"];
  type?: string;
  selectedUuidSet: Set<string>;
}

// Keep existing behavior: draggability and filter flags are stored directly on the items.
/* eslint-disable no-param-reassign */
function applyLibraryFilters(
  itemList: SelectBoxDisplayItem[],
  { filters, freecontents, type, selectedUuidSet }: LibraryFilterParams,
): SelectBoxDisplayItem[] {
  itemList.forEach((item) => {
    item.isDraggable = true;

    filters?.forEach((filter) => {
      if (filter === "not_free" || filter === "not_free_registered") {
        // freecontents is never populated (dead redux slice): inert filter; fixing it needs the full free-media list, which MediaAPI.getFreeMediaList only exposes paginated.
        item.isFree = isFree(item, freecontents);

        if (item.isFree && getFreeType(item, freecontents) === type) {
          item.isFree = false;
        }

        item.isDraggable = item.isDraggable && !item.isFree;
      }

      if (filter === "not_offers") {
        item.isDraggable = item.isDraggable && !item.offers_count;
      }

      if (filter === "not_in_offer") {
        // isOffer holds offers_count itself when falsy.
        item.isOffer = item.offers_count && selectedUuidSet.has(item.uuid);
        item.isDraggable = item.isDraggable && !item.isOffer;
      }
    });
  });

  return itemList;
}
/* eslint-enable no-param-reassign */

export function useLibraryItems({
  selectedItems,
  filters,
  ignoreSmartlistsAudios,
  selectBoxType,
  hasMediaList,
  hasCastingList,
  hasSmartlistList,
  hasDisplayList,
}: UseLibraryItemsParams): UseLibraryItemsResult {
  const { projectSlug } = useParams<{ projectSlug: string }>();

  const getCastingsQuery = MediaAPI.generateQuery({
    fn: MediaAPI.getCastings,
    params: [{ perPage: 100000 }],
  });

  const { data: castingsData, isFetched: isFetchedCastings } = useQuery({
    ...getCastingsQuery,
    enabled: hasCastingList,
  });

  const getDisplayListQuery = SmartlistAPI.generateQuery({
    fn: SmartlistAPI.getDisplayList,
    params: [
      {
        projectSlug,
      },
    ],
  });

  const { data: displaysData, isFetched: isFetchedDisplays } = useQuery({
    ...getDisplayListQuery,
    enabled: hasDisplayList,
  });

  const contents = useSelector(
    (state: AppState) => state.fetchReducer.contents,
  );
  const smartlists = useSelector(
    (state: AppState) => state.fetchReducer.smartlists,
  );
  const freecontents = useSelector(
    (state: AppState) => state.fetchReducer.freecontents,
  );

  const { type } = useParams<{ type?: string }>();
  const [libraryStatus, setLibraryStatus] = useState("all");
  const [librarySearch, setLibrarySearch] = useState("");
  const [selectedTab, setSelectedTab] = useState<SelectBoxTab | undefined>();

  const isLoading =
    (hasMediaList && !contents.isFetched) ||
    (hasSmartlistList && !smartlists.isFetched) ||
    (hasDisplayList && !isFetchedDisplays) ||
    (hasCastingList && !isFetchedCastings);

  // Keyed on membership only: a rank change must not rebuild the whole library.
  const selectedUuidKey = selectedItems
    .map((selectedItem) => selectedItem.uuid)
    .sort()
    .join("|");
  const selectedUuidSet = useMemo(
    () => new Set(selectedUuidKey.split("|")),
    [selectedUuidKey],
  );

  const isSelectedContent = useCallback(
    (content: { uuid: string }) => selectedUuidSet.has(content.uuid),
    [selectedUuidSet],
  );

  const castingList = useMemo(() => {
    const libraryCasting: SelectBoxDisplayItem[] =
      castingsData?.data?.reduce<SelectBoxDisplayItem[]>((acc, cast) => {
        if (!isSelectedContent(cast)) {
          acc.push({
            ...cast,
            isChecked: false,
            itemType: "casting",
            isDraggable: true,
            status: ItemStatus.getStatusByType(cast, "casting"),
          });
        }
        return acc;
      }, []) ?? [];

    return libraryCasting;
  }, [castingsData?.data, isSelectedContent]);

  const displayList = useMemo(() => {
    const newDisplays: SelectBoxDisplayItem[] = [];

    displaysData?.data?.forEach((content) => {
      if (!isSelectedContent(content)) {
        newDisplays.push({
          ...content,
          isChecked: false,
          itemType: "display",
          isDraggable: true,
          status: ItemStatus.getStatusByType(content, "display"),
        });
      }
    });

    return newDisplays;
  }, [displaysData?.data, isSelectedContent]);

  const getContents = useCallback(
    (audio = false): SelectBoxDisplayItem[] =>
      contents.data
        .map(
          (content): SelectBoxDisplayItem => ({
            ...content,
            isChecked: false,
            itemType: "video",
            isDraggable: true,
            status: ItemStatus.getStatusByType(content, "video"),
          }),
        )
        .filter((content) => {
          if (!isSelectedContent(content) && content.status !== "archived") {
            const isAudio = isAudioContent(content);

            if (audio) {
              return isAudio;
            }

            switch (selectBoxType) {
              case OFFERS:
              case FREE:
                return !content.master_media_uuid;
              case SMARTLISTS:
              case SEASONS:
                return content.type !== MASTER;
              default:
                return !isAudio;
            }
          }

          return false;
        }),
    [contents.data, isSelectedContent, selectBoxType],
  );

  const getSmartlist = useCallback(
    (smartlistType: SmartlistType): SelectBoxDisplayItem[] => {
      const newSmartlist: SelectBoxDisplayItem[] = [];

      smartlists.data.forEach((content) => {
        if (!isSelectedContent(content) && content.type === smartlistType) {
          newSmartlist.push({
            ...content,
            isChecked: false,
            itemType: "smartlist",
            isDraggable: true,
            status: ItemStatus.getStatusByType(content, "smartlist"),
          });
        }
      });

      return newSmartlist;
    },
    [smartlists.data, isSelectedContent],
  );

  // While loading, the unresolved selected seed items render as spinners.
  const loadingItemList = isLoading ? selectedItems : null;

  const libraryItems = useMemo((): SelectBoxDisplayItem[] => {
    if (loadingItemList) return [...loadingItemList];

    switch (selectedTab?.key) {
      case "audios":
        return ignoreSmartlistsAudios
          ? getContents(true)
          : [...getSmartlist(SmartlistType.AUDIO), ...getContents(true)];
      case "castings":
        return castingList;
      case "contents":
        return getContents();
      case "displays":
        return displayList;
      case "standard":
        return [
          ...getSmartlist(SmartlistType.STANDARD),
          ...getSmartlist(SmartlistType.AUTO),
        ];
      case "series":
      case "seasons":
        return [
          ...getSmartlist(SmartlistType.SERIES),
          ...getSmartlist(SmartlistType.SEASON),
        ];
      default:
        return [];
    }
  }, [
    loadingItemList,
    selectedTab,
    ignoreSmartlistsAudios,
    getContents,
    getSmartlist,
    castingList,
    displayList,
  ]);

  const libraryFilteredItems = useMemo((): SelectBoxDisplayItem[] => {
    const statusFilteredList = libraryItems.filter(
      (item) => libraryStatus === "all" || item.status === libraryStatus,
    );
    const searchedList = librarySearch
      ? searchLibraryItems(statusFilteredList, librarySearch.toLowerCase())
      : statusFilteredList;

    return applyLibraryFilters(searchedList, {
      filters,
      freecontents,
      type,
      selectedUuidSet,
    });
  }, [
    libraryItems,
    libraryStatus,
    librarySearch,
    filters,
    freecontents,
    type,
    selectedUuidSet,
  ]);

  return {
    libraryItems,
    libraryStatus,
    setLibraryStatus,
    librarySearch,
    setLibrarySearch,
    selectedTab,
    setSelectedTab,
    libraryFilteredItems,
  };
}
